import type { ComputeEvaluator } from "./compute-evaluator"

export interface ExpressionFunction {
  name: string
  pure: boolean
  params: Record<string, FunctionParam>
  steps: Record<string, string> // step name -> expression
  return: string
}

export interface FunctionParam {
  type: string
  default?: unknown
  required?: boolean
}

/**
 * Registry for JSON-declared expression functions.
 * Functions are evaluated using the safe ComputeEvaluator (no eval/new Function).
 */
export class FunctionRegistry {
  private functions = new Map<string, ExpressionFunction>()

  register(fn: ExpressionFunction): void {
    this.functions.set(fn.name, fn)
  }

  registerFromJson(json: Record<string, unknown>): void {
    const funcs = json as Record<string, Record<string, unknown>>
    for (const [name, def] of Object.entries(funcs)) {
      this.register({
        name,
        pure: (def.pure as boolean) ?? true,
        params: (def.params as Record<string, FunctionParam>) ?? {},
        steps: (def.steps as Record<string, string>) ?? {},
        return: (def.return as string) ?? "",
      })
    }
  }

  get(name: string): ExpressionFunction | undefined {
    return this.functions.get(name)
  }

  has(name: string): boolean {
    return this.functions.has(name)
  }

  /** List all registered function names. */
  list(): string[] {
    return Array.from(this.functions.keys())
  }

  /**
   * Evaluate a registered function with given arguments.
   * Steps are evaluated sequentially, building up a local scope.
   * The `return` expression is evaluated last with the full scope.
   */
  evaluate(
    name: string,
    args: unknown[],
    state: Record<string, unknown>,
    evaluator: ComputeEvaluator,
  ): unknown {
    const fn = this.functions.get(name)
    if (!fn) throw new Error(`Function not found: ${name}`)

    // Bind params to args
    const localScope: Record<string, unknown> = {}
    const paramNames = Object.keys(fn.params)
    paramNames.forEach((p, i) => {
      localScope[p] = i < args.length ? args[i] : fn.params[p]?.default
    })

    // Evaluate steps sequentially, each step can reference prior steps
    for (const [stepName, expr] of Object.entries(fn.steps)) {
      localScope[stepName] = evaluateWithScope(expr, { ...state, ...localScope }, evaluator)
    }

    // Evaluate return expression
    return evaluateWithScope(fn.return, { ...state, ...localScope }, evaluator)
  }
}

/**
 * Evaluate an expression string by substituting scope variables into it.
 * Simple variable references like `price * quantity` are resolved from scope.
 */
function evaluateWithScope(
  expr: string,
  scope: Record<string, unknown>,
  evaluator: ComputeEvaluator,
): unknown {
  // Replace simple identifiers with their scope values
  let processed = expr
  for (const [key, value] of Object.entries(scope)) {
    if (value === undefined || value === null) continue
    // Replace standalone identifier occurrences (word boundary)
    const regex = new RegExp(`\\b${escapeRegex(key)}\\b`, "g")
    if (typeof value === "number" || typeof value === "boolean") {
      processed = processed.replace(regex, String(value))
    } else if (typeof value === "string") {
      // If the expression is purely this key, return the string directly
      if (expr.trim() === key) return value
      processed = processed.replace(regex, `'${value}'`)
    }
  }

  return evaluator.evaluate(processed)
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}
